import { useContext, useEffect } from 'react'

import styled from '@emotion/styled'

import {
  ArcadeButton,
  BaseCell,
  PaddingInset,
  PushCell,
  SemanticColors,
  spacing,
  Stack,
  Text,
} from '@cash-design-system/react'
import {
  FormBlockerElementSelectableRowElement,
  FormBlockerElementSelectableRowElementAppearance,
  FormBlockerElementSelectableRowElementDetailTextStyle,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useTextTokenizer } from '@cash-web/shared-feature-use-text-tokenizer'
import { getBlockerAction } from '@cash-web/shared-flow-feature-main'
import { removeMarkdown } from '@cash-web/text-tokenizer'
import { WebViewUnsupportedFeature } from '@squareup/customer-data-events-web'

import { FormBlockerContext } from '../../FormBlockerContext'
import { selectableRowElementAsset } from './selectableRowElementAsset'

const Container = styled.div<{ isInteractive: boolean }>`
  margin: ${({ isInteractive }) => (isInteractive ? '0.375rem 0' : '0')};
`
const getColors = ({
  rowAppearance,
  detailTextStyle,
}: {
  rowAppearance?: FormBlockerElementSelectableRowElementAppearance
  detailTextStyle?: FormBlockerElementSelectableRowElementDetailTextStyle
}) => {
  let subtitleTextColor: SemanticColors = 'textSubtle'
  const detailTextColor: SemanticColors =
    detailTextStyle === FormBlockerElementSelectableRowElementDetailTextStyle.CRITICAL ? 'textDanger' : 'textBrand'
  let iconColor: SemanticColors = 'iconStandard'

  if (rowAppearance === FormBlockerElementSelectableRowElementAppearance.DISABLED) {
    subtitleTextColor = 'textDisabled'
    iconColor = 'iconSubtle'
  } else if (rowAppearance === FormBlockerElementSelectableRowElementAppearance.TINTED) {
    iconColor = 'iconBrand'
  }

  return {
    subtitleTextColor,
    detailTextColor,
    iconColor,
  }
}

type SelectableRowElementVariant = 'labeled-button' | 'unlabeled-button' | 'not-button'

const SelectableRowElementBody = ({
  subtitle,
  subtitleTextColor,
  detail_text,
  detailTextColor,
}: {
  subtitle?: string
  subtitleTextColor: SemanticColors
  detail_text?: string
  detailTextColor: SemanticColors
}) => {
  const textTokenizer = useTextTokenizer()
  const tokenizedSubtitle = textTokenizer(subtitle)
  const tokenizedDetailText = textTokenizer(detail_text)

  if (!subtitle && !detail_text) {
    return null
  }

  return (
    <Stack space={spacing.xxs}>
      {subtitle && (
        <Text typography="body" color={subtitleTextColor}>
          {tokenizedSubtitle}
        </Text>
      )}
      {detail_text && (
        <Text typography="labelMedium" color={detailTextColor}>
          {tokenizedDetailText}
        </Text>
      )}
    </Stack>
  )
}

const SelectableRowElement = ({
  icon,
  local_icon,
  avatar,
  title,
  subtitle,
  accessibility_text,
  detail_text,
  detail_text_style,
  appearance,
  remote_image,
  row_action,
}: FormBlockerElementSelectableRowElement) => {
  const logCdfEvent = useLogCashWebCdfEvent()
  const context = useContext(FormBlockerContext)
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  // Remove markdown formatting from title since styling should be handled by the component
  const titleWithoutMarkdown = removeMarkdown(title ?? '')

  const { subtitleTextColor, detailTextColor, iconColor } = getColors({
    rowAppearance: appearance,
    detailTextStyle: detail_text_style,
  })

  const blockerAction = row_action?.action && getBlockerAction(row_action?.action)
  const handleClick = () => {
    if (row_action?.action && blockerAction?.isSupported) {
      context.onFormBlockerAction(row_action?.action)
    }
  }
  useEffect(() => {
    if (row_action?.action && blockerAction?.isSupported === false) {
      logCdfEvent(
        WebViewUnsupportedFeature({
          source: 'SelectableRowElement',
          feature: 'BLOCKER_ACTION',
          action: blockerAction.actionName,
        })
      )
    }
  }, [row_action?.action, blockerAction?.isSupported, logCdfEvent, blockerAction?.actionName])

  const hasSupportedRowAction = row_action?.action && blockerAction?.isSupported
  const variant: SelectableRowElementVariant = !hasSupportedRowAction
    ? 'not-button'
    : row_action.label
      ? 'labeled-button'
      : 'unlabeled-button'

  const asset = selectableRowElementAsset({
    avatar,
    localIcon: local_icon,
    icon,
    iconColor,
    disabled: appearance === FormBlockerElementSelectableRowElementAppearance.DISABLED,
  })

  const body =
    subtitle || detail_text ? (
      <SelectableRowElementBody
        subtitle={subtitle}
        subtitleTextColor={subtitleTextColor}
        detail_text={detail_text}
        detailTextColor={detailTextColor}
      />
    ) : undefined

  const contents = (
    <Container aria-label={accessibility_text} isInteractive={variant !== 'not-button'}>
      {variant === 'not-button' && <BaseCell asset={asset} label={titleWithoutMarkdown} body={body} />}
      {variant === 'labeled-button' && (
        <BaseCell
          label={titleWithoutMarkdown}
          disabled={appearance === FormBlockerElementSelectableRowElementAppearance.DISABLED}
          asset={asset}
          body={body}
          accessory={
            <ArcadeButton
              prominence="standard"
              size="compact"
              onClick={handleClick}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  handleClick()
                }
              }}
            >
              {row_action?.label}
            </ArcadeButton>
          }
        />
      )}
      {variant === 'unlabeled-button' && (
        <PushCell
          label={titleWithoutMarkdown}
          disabled={appearance === FormBlockerElementSelectableRowElementAppearance.DISABLED}
          cellProps={{
            asset,
            body,
          }}
          onClick={handleClick}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              handleClick()
            }
          }}
        />
      )}
    </Container>
  )
  if (isArcadeMigrationEnabled) return contents
  return <PaddingInset>{contents}</PaddingInset>
}

export default SelectableRowElement
